import authenticateService from './authenticateService';
import {
  getTokenFromSessionStorage,
  getUserNameFromSessionStorage
} from './session-storage-service';

/** Called as bytes arrive, so a long export can show that it is still moving. */
export type DownloadProgress = (bytesReceived: number) => void;

/**
 * Human-readable byte count for progress text.
 * @param bytes - Bytes received so far.
 * @returns Compact size like "12.4 MB".
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Hand a finished blob to the browser as a download.
 * @param blob - The file contents.
 * @param filename - Suggested filename.
 */
function saveBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

/**
 * Open the browser's save dialog, if it has one.
 *
 * This has to happen before the request starts: the picker needs the click's
 * transient activation, which an awaited fetch would consume.
 * @param filename - Suggested filename.
 * @returns A file handle, or null when unsupported; undefined when the user cancelled.
 */
async function openSaveDialog(filename: string): Promise<FileSystemFileHandle | null | undefined> {
  const picker = (
    window as unknown as {
      showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle>;
    }
  ).showSaveFilePicker;
  if (!picker) return null;
  try {
    return await picker.call(window, {
      suggestedName: filename,
      types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }]
    });
  } catch {
    return undefined;
  }
}

/**
 * Download a server-streamed CSV export, reporting progress as it arrives.
 *
 * The rows are streamed by the server, so this reads the response chunk by chunk
 * rather than buffering it: where the browser supports the file-system API the
 * chunks go straight to the file the user picked, which keeps a multi-million-row
 * export off the JS heap entirely. Browsers without it collect the chunks and
 * fall back to a Blob, which does buffer.
 *
 * These exports can run for minutes, so every chunk reports the running byte
 * count — without it a long download is indistinguishable from a hung one.
 * @param url - Fully-qualified export URL including query parameters.
 * @param filename - Suggested filename.
 * @param onProgress - Optional per-chunk progress callback.
 * @returns Resolves once the file is written, or immediately if the user cancels.
 */
export async function downloadStreamedCsv(
  url: string,
  filename: string,
  onProgress?: DownloadProgress
): Promise<void> {
  const handle = await openSaveDialog(filename);
  if (handle === undefined) {
    // The user dismissed the save dialog: nothing to download.
    return;
  }

  /**
   * One attempt, with whatever token is in session storage right now.
   * @returns The raw fetch response, whatever its status.
   */
  const attempt = (): Promise<Response> =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getTokenFromSessionStorage()}`,
        user_name: getUserNameFromSessionStorage()
      }
    });

  // This is a raw `fetch`, so it does NOT go through the axios interceptor and
  // gets none of its token refresh. Exports are the single most likely request
  // to meet an expired token — they are run after a long session of looking at
  // charts — and without this a stale token surfaced as a bare "Export failed
  // with status 401" with no way to recover but a reload. Same bug that made
  // the live SSE stream claim the user was not authorised.
  let response = await attempt();
  if (response.status === 401 || response.status === 403) {
    const { error } = await authenticateService.refreshToken();
    if (!error) {
      response = await attempt();
    }
  }
  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  if (!response.body) {
    // No readable stream (very old browser): take the whole body at once.
    saveBlob(await response.blob(), filename);
    return;
  }

  const reader = response.body.getReader();
  const writable = handle ? await handle.createWritable() : null;
  // Only used by the no-file-handle fallback, which buffers by definition.
  const chunks: BlobPart[] = [];
  let received = 0;

  try {
    for (;;) {
      // Sequential by nature: each chunk must be written before the next is read.
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        received += value.byteLength;
        onProgress?.(received);
        if (writable) {
          // eslint-disable-next-line no-await-in-loop
          await writable.write(value);
        } else {
          // Copy into a plain ArrayBuffer: the reader may reuse its view.
          chunks.push(new Uint8Array(value).buffer);
        }
      }
    }
    if (writable) {
      await writable.close();
    } else {
      saveBlob(new Blob(chunks, { type: 'text/csv' }), filename);
    }
  } catch (e) {
    // Leave no half-written file behind when the stream fails or is cut short by
    // a server-side timeout.
    if (writable) {
      await writable.abort().catch(() => undefined);
    }
    throw e;
  }
}
